import { ErrorHandler, Injectable } from '@angular/core';

// Recovers from stale-deploy chunk failures. After a new deployment, a client
// holding a cached index.html (notably the Telegram WebView, which ignores the
// no-cache headers nginx sets) may request hashed lazy-chunk filenames that no
// longer exist -> the dynamic import 404s, the route never activates, and the
// user is left on the black astronaut loader (a stuck full-screen overlay).
// Reloading pulls a fresh index.html with the correct chunk hashes, self-
// healing the state instead of black-screening.

// Don't reload more than once per window. If a chunk is genuinely gone (not
// just stale), the second failure within this window falls through to the
// default handler instead of looping the page forever.
const RELOAD_GUARD_KEY = 'smc.chunkReloadAt';
const RELOAD_GUARD_MS = 30_000;

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (this._isChunkLoadError(error) && this._canReload()) {
      try {
        sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      } catch {/* private mode / quota — reload anyway */}
      this._reload();
      return;
    }
    console.error(error);
  }

  // Seam so specs can assert the recovery without navigating the test runner:
  // `window.location.reload` is non-configurable and non-writable in Chrome,
  // so it cannot be spied on directly.
  protected _reload(): void {
    window.location.reload();
  }

  // Matches both the webpack ChunkLoadError shape and the esbuild/native
  // dynamic-import rejection messages Angular emits when a lazy route's chunk
  // fails to load. The router surfaces a failed loadComponent() import to the
  // global ErrorHandler, so this is the single place that catches it.
  private _isChunkLoadError(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | null;
    const name = err?.name ?? '';
    const message = err?.message ?? '';
    return (
      name === 'ChunkLoadError' ||
      /loading chunk \S+ failed/i.test(message) ||
      /failed to fetch dynamically imported module/i.test(message) ||
      /error loading dynamically imported module/i.test(message) ||
      /importing a module script failed/i.test(message)
    );
  }

  private _canReload(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? 0);
      return !last || Date.now() - last > RELOAD_GUARD_MS;
    } catch {
      return true; // can't read guard (private mode) — allow the reload
    }
  }
}
